'use client';
import {Box} from '@mui/material';
import {styled, useTheme, type SxProps, type Theme} from '@mui/material/styles';
import NextImage, {getImageProps} from 'next/image';
import {useCallback, useEffect, useRef, useState, type FC} from 'react';
import {preconnect, preload} from 'react-dom';
import {MediaType} from '../../model';
import {SoundButton, Typography} from '../primitives';
import {HERO_OVERHANG_VAR} from './heroSize';
import type {HeroContentProps, HeroSectionMedia} from './types';

// Full-bleed hero: the background image always spans the viewport width.
const BACKGROUND_SIZES = '100vw';
const VIDEO_PRELOAD = 'none';
const PLAYBACK_IDLE_TIMEOUT_MS = 2000;
const LAYER_DISPLAY = 'block';
const TOGGLE_DISPLAY = 'inline-flex';
const HIDDEN = 'none';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
// 1x1 transparent GIF: the <img> inside the poster <picture> must have a src, but the
// real bytes come from whichever <source media> matches, so the fallback stays empty.
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

export interface HeroStageProps extends HeroContentProps {
  // False while the hero is off screen — scrolled past, or covered by the curtain:
  // playback pauses and the sound drops until it comes back.
  inView: boolean;
}

const BackgroundImage = styled(NextImage)({
  position: 'absolute',
  inset: 0,
  zIndex: -2,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}) as FC<{src: string; alt: string; fill: true; sizes: string; priority?: boolean; sx?: SxProps<Theme>}>;

const BackgroundPicture = styled('picture')({
  position: 'absolute',
  inset: 0,
  zIndex: -2,
  '& img': {objectFit: 'cover'},
});

// Sits under the video layer: the poster is what paints (and counts as LCP) until the
// video's first frame decodes, then the video covers it.
const BackgroundPoster = styled(BackgroundPicture)({
  zIndex: -3,
});

const BackgroundVideo = styled('video')({
  position: 'absolute',
  inset: 0,
  zIndex: -2,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

type Viewport = 'web' | 'mobile';

function displayOn(viewport: Viewport | undefined, visible: string) {
  if (!viewport) {
    return visible;
  }
  return viewport === 'mobile' ? {xs: visible, md: HIDDEN} : {xs: HIDDEN, md: visible};
}

function stripMediaPrefix(query: string) {
  return query.replace(/^@media\s*/, '');
}

function originOf(src: string): string | undefined {
  try {
    return new URL(src).origin;
  } catch {
    return undefined;
  }
}

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

// Hero videos are CMS uploads of tens of MB. On a metered or slow connection that
// download is worse than the poster/dark fallback + heading, so the sources are
// dropped before the browser commits to them.
function shouldSkipVideo(): boolean {
  const connection = (navigator as Navigator & {connection?: NetworkInformation}).connection;
  return Boolean(connection?.saveData) || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g';
}

// Runs the callback once the page has loaded and the main thread is idle, so the hero
// video never competes with the critical resources. Returns a cancel function.
function afterPageLoad(callback: () => void): () => void {
  let idleHandle: number | undefined;
  let timeoutHandle: number | undefined;
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(callback, {timeout: PLAYBACK_IDLE_TIMEOUT_MS});
    } else {
      timeoutHandle = window.setTimeout(callback, 0);
    }
  };
  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, {once: true});
  }
  return () => {
    window.removeEventListener('load', schedule);
    if (idleHandle !== undefined) {
      window.cancelIdleCallback(idleHandle);
    }
    if (timeoutHandle !== undefined) {
      window.clearTimeout(timeoutHandle);
    }
  };
}

const Scrim = styled(Box)(({theme}) => ({
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  background: theme.palette.colors.surface.darkOverlay,
}));

const Content = styled(Box)(({theme}) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  maxWidth: 600,
  paddingInline: theme.spacing(2.25),
  [theme.breakpoints.up('md')]: {paddingInline: 0},
}));

const Actions = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.up('md')]: {flexDirection: 'row'},
}));

// 48px dark-scrim toggle pinned to the hero's bottom-end corner, clear of the
// overhang the curtain slides over. Sits at `opacity: 0.8` and fades to full on
// hover — VideoPlayer's UnmuteOverlay shares the same scrim without the fade.
const muteToggleSx = (theme: Theme) => ({
  position: 'absolute' as const,
  zIndex: 1,
  bottom: `calc(var(${HERO_OVERHANG_VAR}) + ${theme.spacing(2)})`,
  insetInlineEnd: theme.spacing(2),
  opacity: 0.8,
  transition: theme.transitions.create('opacity'),
  '&:hover': {opacity: 1},
});

export function HeroStage({media, mobileMedia, title, actions, inView}: HeroStageProps) {
  const theme = useTheme();
  const webVideoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const [soundOn, setSoundOn] = useState(false);
  const [videoSkipped, setVideoSkipped] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const skipChecked = useRef(false);

  useEffect(
    () =>
      afterPageLoad(() => {
        setPageLoaded(true);
      }),
    [],
  );

  // A play() that loses a race with the pause on the way out rejects with AbortError and
  // is nothing to act on. A browser that refuses to play *with sound* is: drop the sound
  // rather than leave the hero on a frozen frame under a button that says it is playing.
  const play = useCallback((video: HTMLVideoElement) => {
    video.play().catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError' && !video.muted) {
        setSoundOn(false);
        video.muted = true;
        void video.play().catch(() => undefined);
      }
    });
  }, []);

  const mobile = mobileMedia && mobileMedia.src !== media.src ? mobileMedia : undefined;
  const webIsVideo = media.type === MediaType.Video;
  const mobileIsVideo = mobile ? mobile.type === MediaType.Video : webIsVideo;
  const hasVideo = webIsVideo || mobileIsVideo;
  const mdUp = stripMediaPrefix(theme.breakpoints.up('md'));
  const mdDown = stripMediaPrefix(theme.breakpoints.down('md'));
  const split = Boolean(mobile);
  // Autoplay only ever starts muted — browsers block a video that starts with sound.
  // The user's unmute survives scrolling past the hero and is restored on the way back.
  const muted = !soundOn || !inView;

  // A video without a poster paints nothing until enough of it has downloaded, which
  // makes it the page's Largest Contentful Paint. The CMS has no poster field, so when
  // the other viewport's media is an image it doubles as the poster.
  const webPoster = media.poster ?? (mobile && !mobileIsVideo ? mobile.src : undefined);
  const mobilePoster = mobile?.poster ?? media.poster ?? (!webIsVideo ? media.src : undefined);

  const mediaOrigin = originOf(media.src);
  if (mediaOrigin) {
    preconnect(mediaOrigin);
  }

  // Every layer uses `preload="none"` and only this effect starts playback: after the page
  // has loaded, while the hero is on screen, and for the layer matching the viewport. A
  // visitor who leaves or scrolls away first never downloads the video. Never add
  // `autoPlay` to these elements — it would bypass all three gates.
  useEffect(() => {
    if (!hasVideo) {
      return;
    }
    if (videoSkipped) {
      return;
    }
    if (!skipChecked.current) {
      skipChecked.current = true;
      if (shouldSkipVideo()) {
        for (const video of [webVideoRef.current, mobileVideoRef.current]) {
          video?.removeAttribute('src');
          video?.load();
        }
        setVideoSkipped(true);
        return;
      }
    }
    const reducedMotion = window.matchMedia(REDUCED_MOTION);
    const entries = [
      {video: webVideoRef.current, query: split ? window.matchMedia(mdUp) : null},
      {video: mobileVideoRef.current, query: split ? window.matchMedia(mdDown) : null},
    ].filter((entry): entry is {video: HTMLVideoElement; query: MediaQueryList | null} => Boolean(entry.video));

    const sync = () => {
      for (const {video, query} of entries) {
        // React does not reliably re-apply `muted` to a mounted <video>, so it is set here.
        video.muted = muted;
        if (pageLoaded && inView && (!query || query.matches) && !reducedMotion.matches) {
          play(video);
        } else {
          video.pause();
        }
      }
    };
    sync();

    const queries = [reducedMotion, ...entries.map(entry => entry.query).filter((query): query is MediaQueryList => query !== null)];
    for (const query of queries) {
      query.addEventListener('change', sync);
    }
    return () => {
      for (const query of queries) {
        query.removeEventListener('change', sync);
      }
    };
  }, [hasVideo, split, mdUp, mdDown, inView, muted, videoSkipped, pageLoaded, play]);

  const toggleMute = () => {
    setSoundOn(current => !current);
  };

  const alt = media.alt ?? title ?? '';

  // The poster is rendered as its own <picture> (through the image optimizer) rather
  // than the video `poster` attribute, which would fetch the raw CMS upload.
  // A <picture> is invisible to the preload scanner until it is parsed, so each poster is
  // hinted from <head>, scoped to its breakpoint so a viewport only fetches its own.
  const preloadPoster = (props: {src?: string; srcSet?: string}, mediaQuery?: string) => {
    if (!props.src) {
      return;
    }
    preload(props.src, {as: 'image', imageSrcSet: props.srcSet, imageSizes: BACKGROUND_SIZES, fetchPriority: 'high', media: mediaQuery});
  };

  const renderPoster = () => {
    if (!split) {
      if (!webIsVideo || !webPoster) {
        return null;
      }
      const {
        props: {alt: _posterAlt, ...posterProps},
      } = getImageProps({src: webPoster, alt: '', fill: true, sizes: BACKGROUND_SIZES, priority: true});
      preloadPoster(posterProps);
      return (
        <BackgroundPoster aria-hidden>
          <img {...posterProps} fetchPriority="high" alt="" />
        </BackgroundPoster>
      );
    }
    const layers = [
      {media: mdUp, src: webIsVideo ? webPoster : undefined},
      {media: mdDown, src: mobileIsVideo ? mobilePoster : undefined},
    ].filter((layer): layer is {media: string; src: string} => Boolean(layer.src));
    if (layers.length === 0) {
      return null;
    }
    const sources = layers.map(layer => ({
      media: layer.media,
      props: getImageProps({src: layer.src, alt: '', fill: true, sizes: BACKGROUND_SIZES, priority: true}).props,
    }));
    // React dedupes preloads by URL, so when both breakpoints share one poster a single
    // unscoped hint is emitted; a media-scoped pair would silently lose the second one.
    const shared = new Set(sources.map(source => source.props.src)).size === 1;
    for (const source of shared ? sources.slice(0, 1) : sources) {
      preloadPoster(source.props, shared ? undefined : source.media);
    }
    const {alt: _fallbackAlt, src: _fallbackSrc, srcSet: _fallbackSrcSet, ...fallbackProps} = sources[0].props;
    return (
      <BackgroundPoster aria-hidden>
        {sources.map(source => (
          <source key={source.media} media={source.media} srcSet={source.props.srcSet ?? source.props.src} sizes={BACKGROUND_SIZES} />
        ))}
        <img {...fallbackProps} src={TRANSPARENT_PIXEL} fetchPriority="high" alt="" />
      </BackgroundPoster>
    );
  };

  const renderVideo = (source: HeroSectionMedia, viewport: Viewport | undefined, ref: typeof webVideoRef) => (
    <BackgroundVideo
      ref={ref}
      src={source.src}
      muted={muted}
      loop
      playsInline
      preload={VIDEO_PRELOAD}
      aria-hidden
      sx={{display: displayOn(viewport, LAYER_DISPLAY)}}
    />
  );

  const renderImage = (source: HeroSectionMedia, viewport: Viewport | undefined) => (
    <BackgroundImage
      src={source.src}
      alt={source.alt ?? alt}
      fill
      sizes={BACKGROUND_SIZES}
      priority
      sx={{display: displayOn(viewport, LAYER_DISPLAY)}}
    />
  );

  const renderBackground = () => {
    if (!mobile) {
      return webIsVideo ? renderVideo(media, undefined, webVideoRef) : renderImage(media, undefined);
    }
    if (!webIsVideo && !mobileIsVideo) {
      const {
        props: {srcSet: webSrcSet},
      } = getImageProps({src: media.src, alt, fill: true, sizes: BACKGROUND_SIZES, priority: true});
      const {
        props: {alt: mobileAlt, ...mobileImageProps},
      } = getImageProps({src: mobile.src, alt: mobile.alt ?? alt, fill: true, sizes: BACKGROUND_SIZES, priority: true});
      return (
        <BackgroundPicture>
          <source media={mdUp} srcSet={webSrcSet} sizes={BACKGROUND_SIZES} />
          <img {...mobileImageProps} alt={mobileAlt} />
        </BackgroundPicture>
      );
    }
    return (
      <>
        {webIsVideo ? renderVideo(media, 'web', webVideoRef) : renderImage(media, 'web')}
        {mobileIsVideo ? renderVideo(mobile, 'mobile', mobileVideoRef) : renderImage(mobile, 'mobile')}
      </>
    );
  };

  const muteToggleDisplay = split
    ? {xs: mobileIsVideo ? TOGGLE_DISPLAY : HIDDEN, md: webIsVideo ? TOGGLE_DISPLAY : HIDDEN}
    : TOGGLE_DISPLAY;

  return (
    <>
      {renderPoster()}
      {renderBackground()}
      <Scrim />

      <Content>
        {title && (
          <Typography
            size="headline-1"
            weight="bold"
            mobileSize="title-1"
            as="h1"
            sx={{textTransform: 'capitalize', color: theme => theme.palette.colors.text.neutral.inverse, whiteSpace: 'pre-line'}}
          >
            {title}
          </Typography>
        )}

        {actions && <Actions>{actions}</Actions>}
      </Content>

      {hasVideo && !videoSkipped && (
        <SoundButton muted={muted} size={42} onClick={toggleMute} sx={theme => ({...muteToggleSx(theme), display: muteToggleDisplay})} />
      )}
    </>
  );
}

HeroStage.displayName = 'HeroStage';
